import { v4 as uuidv4 } from 'uuid';
import { defaultUnitsV2 } from '../../../../sustell_15/utils/unit-utils';
import { UserProfilePrefs } from '../../../Helpers/UserProfilePrefs';
import { floatValueInDefaultUnit } from './floatHelperFunctions';
import {
    ShrimpProcessing,
    ShrimpProcessingInput,
    ShrimpProcessingInputType,
    ShrimpProcessingOutput,
    ShrimpProcessingOutputItem,
    ShrimpProcessingPackaging,
    ShrimpProcessingPackagingItem,
    TransportTypeDistanceIce,
} from "../../../../sustell_15/models/Baseline/ShrimpBaseline";
import {
    ProcessingPackagingItemDBModel,
    StageType
} from "../../../../sustell_15/models/TempBackendModels/TempProcessingDB";
import {
    ProcessingInputItemDBModel,
    ShrimpPackagingDBModel,
    ShrimpProcessingDBModel,
    ShrimpProcessingInputDBModel,
    ShrimpProcessingOutputDBModel,
    ShrimpProcessingOutputItemDBModel,
    ShrimpProcessingPackagingDBModel,
    ShrimpProcessingStageDataDBModel,
    TransportWithIceDBModel
} from "../../../../sustell_15/models/TempBackendModels/TempShrimpDB";
import {
    formatMassInput,
    formatTransportInput,
    removeNullOrUndefined
} from './outMapperHelpers';
import { ProcessingPackagingItem } from '../../../../sustell_15/models/Facility/Processing';
import {
    PrimaryPackagingType,
    SecondaryPackagingType,
    TertiaryPackagingType
} from '../../../../../graphql/generated/blonk/shrimp';

const mapProcessingInputForOneStage = (
    inputBlockData: ShrimpProcessingInput
): ShrimpProcessingInputDBModel => {
    if (!inputBlockData) return {} as ShrimpProcessingInputDBModel;

    const userPrefs = UserProfilePrefs.getInstance();
    const userUOM = userPrefs.getUserUnitPrefs();
    const outputMassPrecision = userPrefs.getUnitOutputMassPrecision();

    const mappedProcessingInput: ShrimpProcessingInputDBModel = {
      averageWeight: floatValueInDefaultUnit(
        inputBlockData.averageWeight,
        userUOM.unitBarnSmallQuantityUnits,
        defaultUnitsV2.unitBarnSmallQuantityUnits,
        outputMassPrecision,
      ),
      sources: [],
    };

    inputBlockData.sources.forEach((shrimpSource: ShrimpProcessingInputType) => {
        const source: ProcessingInputItemDBModel = {
            id: shrimpSource.id || uuidv4(),
            originStageId: shrimpSource.originStageId,
            originOutputId: shrimpSource.originOutputId,
            totalWeight: floatValueInDefaultUnit(
                shrimpSource.totalWeight,
                userUOM.unitOutputMass,
                defaultUnitsV2.unitOutputMass,
                outputMassPrecision
            ),
            transport: []
        }

        shrimpSource.transport.forEach((sourceTransport: TransportTypeDistanceIce) => {
            const transport: TransportWithIceDBModel = {
                id: sourceTransport.id || uuidv4(),
                mode: sourceTransport.transportMode,
                distance: formatTransportInput(sourceTransport.distance, sourceTransport.transportMode),
                iceAmount: floatValueInDefaultUnit(
                    sourceTransport.iceAmount,
                    userUOM.unitOutputMass,
                    defaultUnitsV2.unitOutputMass,
                    outputMassPrecision
                ),
            }

            source.transport.push(transport);
        });

        mappedProcessingInput.sources.push(source);
    });

    return mappedProcessingInput;
}

const mapProcessingOutputForOneStage = (
    outputBlockData: ShrimpProcessingOutput
): ShrimpProcessingOutputDBModel => {
    if (!outputBlockData) return {} as ShrimpProcessingOutputDBModel;

    const mappedProcessingOutput: ShrimpProcessingOutputDBModel = {
        freshMeatAndEdibleOffal: [],
        cat3SlaughterByProducts: [],
        cat1And2MaterialAndWaste: [],
    };

    const mapCategoryOutput = (
        categoryOutput: ShrimpProcessingOutputItem
    ): ShrimpProcessingOutputItemDBModel => {
        if (!categoryOutput) return {} as ShrimpProcessingOutputItemDBModel;

        const mappedOutput: ShrimpProcessingOutputItemDBModel = {
            outputId: categoryOutput.outputId || uuidv4(),
            outputName: categoryOutput.outputName,
            coolingType: categoryOutput.coolingType,
            calculate: categoryOutput.calculate,
            weight: formatMassInput(categoryOutput.weight),
            price: Number(categoryOutput.price),
        }

        return mappedOutput;
    }

    outputBlockData.freshMeatAndEdibleOffal?.forEach((edibleOutput: ShrimpProcessingOutputItem) => {
        const output: ShrimpProcessingOutputItemDBModel = mapCategoryOutput(edibleOutput);
        mappedProcessingOutput.freshMeatAndEdibleOffal?.push(output);
    });

    outputBlockData.cat3SlaughterByProducts?.forEach((cat3Output: ShrimpProcessingOutputItem) => {
        const output: ShrimpProcessingOutputItemDBModel = mapCategoryOutput(cat3Output);
        mappedProcessingOutput.cat3SlaughterByProducts?.push(output);
    });

    outputBlockData.cat1And2MaterialAndWaste?.forEach((cat1and2Output: ShrimpProcessingOutputItem) => {
        const output: ShrimpProcessingOutputItemDBModel = mapCategoryOutput(cat1and2Output);
        mappedProcessingOutput.cat1And2MaterialAndWaste?.push(output);
    });

    return mappedProcessingOutput;
}

const mapProcessingPackagingForOneStage = (
    packagingBlockData: ShrimpProcessingPackaging
): ShrimpProcessingPackagingDBModel => {
    if (!packagingBlockData) return {} as ShrimpProcessingPackagingDBModel;

    const userPrefs = UserProfilePrefs.getInstance();
    const userUOM = userPrefs.getUserUnitPrefs();
    const outputMassPrecision = userPrefs.getUnitOutputMassPrecision();

    const mappedProcessingPackaging: ShrimpProcessingPackagingDBModel = {
        freshMeatAndEdibleOffal: [],
        cat3SlaughterByProducts: [],
        cat1And2MaterialAndWaste: [],
    };

    const mapPackagingCategory = (
        categoryItem: ShrimpProcessingPackagingItem
    ): ShrimpPackagingDBModel => {
        if (!categoryItem) return {} as ShrimpPackagingDBModel;

        const categoryPackaging: ShrimpPackagingDBModel = {
            outputId: categoryItem.outputId,
            primary: [],
            secondary: [],
            tertiary: [],
        };

        categoryItem.primary?.forEach((primary: ProcessingPackagingItem<PrimaryPackagingType>) => {
            const packaging: ProcessingPackagingItemDBModel<PrimaryPackagingType> = {
                id: primary.id || uuidv4(),
                type: primary.type,
                amount: floatValueInDefaultUnit(
                    primary.amount,
                    userUOM.unitOutputMass,
                    defaultUnitsV2.unitOutputMass,
                    outputMassPrecision
                ),
            };

            categoryPackaging.primary?.push(packaging);
        });
    categoryItem.secondary
      ?.filter((secondary) => secondary.type && secondary.amount)
      .forEach((secondary: ProcessingPackagingItem<SecondaryPackagingType>) => {
        const packaging: ProcessingPackagingItemDBModel<SecondaryPackagingType> =
          {
            id: secondary.id || uuidv4(),
            type: secondary.type,
            amount: floatValueInDefaultUnit(
              secondary.amount,
              userUOM.unitOutputMass,
              defaultUnitsV2.unitOutputMass,
              outputMassPrecision,
            ),
          };

        categoryPackaging.secondary?.push(packaging);
      });

    categoryItem.tertiary
        ?.filter((tertiary) => tertiary.type && tertiary.amount)
        .forEach((tertiary: ProcessingPackagingItem<TertiaryPackagingType>) => {
            const packaging: ProcessingPackagingItemDBModel<TertiaryPackagingType> =
            {
                id: tertiary.id || uuidv4(),
                type: tertiary.type,
                amount: floatValueInDefaultUnit(
                tertiary.amount,
                userUOM.unitOutputMass,
                defaultUnitsV2.unitOutputMass,
                outputMassPrecision,
                ),
            };

            categoryPackaging.tertiary?.push(packaging);
        });

        return categoryPackaging;
    }

    packagingBlockData.freshMeatAndEdibleOffal?.forEach((ediblePackaging: ShrimpProcessingPackagingItem) => {
        const category: ShrimpPackagingDBModel = mapPackagingCategory(ediblePackaging);
        mappedProcessingPackaging.freshMeatAndEdibleOffal?.push(category);
    });

    packagingBlockData.cat3SlaughterByProducts?.forEach((cat3Packaging: ShrimpProcessingPackagingItem) => {
        const category: ShrimpPackagingDBModel = mapPackagingCategory(cat3Packaging);
        mappedProcessingPackaging.cat3SlaughterByProducts?.push(category);
    });

    packagingBlockData.cat1And2MaterialAndWaste?.forEach((cat1and2Packaging: ShrimpProcessingPackagingItem) => {
        const category: ShrimpPackagingDBModel = mapPackagingCategory(cat1and2Packaging);
        mappedProcessingPackaging.cat1And2MaterialAndWaste?.push(category);
    });

    return mappedProcessingPackaging;
}

export const mapOutShrimpProcessingStage = (
    incomingData: ShrimpProcessing
): ShrimpProcessingDBModel => {
    if (!incomingData) return {} as ShrimpProcessingDBModel;

    try {
        const mappedStage: ShrimpProcessingDBModel = {
            facilityType: incomingData.facilityType,
            facilityId: incomingData.facilityId,
            type: (incomingData.type as StageType) || StageType.Processing,
            name: incomingData.name,
            id: incomingData?.id || uuidv4(),
            stageData: {
                processingInput: mapProcessingInputForOneStage(
                    incomingData.stageData.processingInput
                ),
                processingOutput: mapProcessingOutputForOneStage(
                    incomingData.stageData.processingOutput
                ),
                processingPackaging: mapProcessingPackagingForOneStage(
                    incomingData.stageData.processingPackaging
                ),
            } as ShrimpProcessingStageDataDBModel,
        };

        return removeNullOrUndefined<ShrimpProcessingDBModel>(mappedStage);
    } catch (err) {
        console.error("Error on mapOutProcessingStage: ", err);
    }

    return {} as ShrimpProcessingDBModel;
};